import { chain } from 'lodash';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import type { XlsxField } from '../../../common/interfaces/xlsx-field';
import type { XlsxTemplate } from '../../../common/interfaces/xlsx-section';
import { DriversLicenseCategory } from '../enums/drivers-license-category';

export const DRIVERS_XLSX_TEMPLATE: XlsxTemplate = [
  {
    name: 'drivers.driverForm.driverData.heading',
    fields: [
      {
        key: 'firstName',
        name: 'labels.firstName',
        required: true,
      },
      {
        key: 'lastName',
        name: 'labels.lastName',
        required: true,
      },
      {
        key: 'peselNumber',
        name: 'labels.peselNumber',
      },
      {
        key: 'birthDate',
        name: 'labels.birthDate',
      },
      {
        key: 'privatePhone',
        name: 'labels.privatePhone',
        required: true,
      },
      {
        key: 'workPhone',
        name: 'labels.workPhone',
      },
      {
        key: 'privateEmail',
        name: 'labels.privateEmail',
      },
      {
        key: 'nationality',
        name: 'labels.nationality',
        required: true,
      },
      {
        key: 'homeAddress',
        name: 'labels.homeAddress',
        required: true,
      },
      {
        key: 'assignedBase',
        name: 'labels.base',
      },
    ],
    required: true,
  },
  {
    name: 'drivers.driverForm.employmentConditions.heading',
    fields: [
      {
        key: 'contractType',
        name: 'labels.contractType',
        required: true,
      },
      {
        key: 'settlementMethod',
        name: 'labels.driverSettlementMethod',
        required: true,
      },
      {
        key: 'dailyRate',
        name: 'labels.dailyRate',
      },
      {
        key: 'dailyRateCurrency',
        name: (translateService) =>
          `${translateService.instant('labels.dailyRate')} (${translateService.instant('labels.currency').toLowerCase()})`,
      },
      {
        key: 'mileageRate',
        name: 'labels.mileageRate',
      },
      {
        key: 'mileageRateCurrency',
        name: (translateService) =>
          `${translateService.instant('labels.mileageRate')} (${translateService.instant('labels.currency').toLowerCase()})`,
      },
      {
        key: 'baseRate',
        name: 'labels.baseRate',
      },
      {
        key: 'baseRateCurrency',
        name: (translateService) =>
          `${translateService.instant('labels.baseRate')} (${translateService.instant('labels.currency').toLowerCase()})`,
      },
      {
        key: 'freightPercentage',
        name: 'labels.freightPercentage',
      },
      {
        key: 'roundTripRate',
        name: 'labels.roundTripRate',
      },
      {
        key: 'roundTripRateCurrency',
        name: (translateService) =>
          `${translateService.instant('labels.roundTripRate')} (${translateService.instant('labels.currency').toLowerCase()})`,
      },
      {
        key: 'contractTermFrom',
        name: 'labels.contractFrom',
        required: true,
      },
      {
        key: 'contractTermTo',
        name: 'labels.contractTo',
        required: true,
      },
      {
        key: 'vatId',
        name: 'labels.vatId',
      },
    ],
    required: true,
  },
  {
    name: 'drivers.driverForm.skills.heading',
    fields: [
      {
        key: 'languages',
        name: 'labels.languages',
      },
      {
        key: 'technicalSkills',
        name: 'labels.driverTechnicalSkills',
      },
      {
        key: 'semiTrailers',
        name: 'labels.semiTrailers',
      },
      {
        key: 'experienceLevel',
        name: 'labels.driverExperienceLevel',
      },
    ],
    required: true,
  },
  {
    name: 'forms.countriesWhitelist.heading',
    fields: [
      {
        key: 'countriesGroup',
        name: 'forms.countriesWhitelist.group',
      },
      {
        key: 'countriesWhitelist',
        name: 'forms.countriesWhitelist.whitelist',
      },
    ],
    required: true,
  },
  {
    name: 'labels.note',
    fields: [
      {
        key: 'note',
        name: 'labels.note',
      },
    ],
  },
  {
    name: 'drivers.driverForm.driversLicenseAndProfessionalQualifications.heading',
    fields: [
      ...chain(DriversLicenseCategory)
        .values()
        .map((category): XlsxField => ({
          key: `driversLicenseCategory.${category}`,
          name: (translateService) =>
            `${translateService.instant('labels.expirationDate')} ${translateService.instant(`${DictionaryCode.DRIVERS_LICENSE_CATEGORY}.${category}`)}`,
        }))
        .value(),
      {
        key: 'professionalQualificationsExpirationDate',
        name: 'labels.professionalQualificationsExpirationDate',
      },
      {
        key: 'driversLicenseNumber',
        name: 'labels.driversLicenseNumber',
        required: true,
      },
      {
        key: 'driversLicenseIssuingCountry',
        name: 'labels.issuingCountry',
      },
    ],
    required: true,
  },
  {
    name: 'drivers.driverForm.driverCard.heading',
    fields: [
      {
        key: 'driverCardNumber',
        name: 'labels.number',
        required: true,
      },
      {
        key: 'driverCardIssuingCountry',
        name: 'labels.issuingCountry',
        required: true,
      },
      {
        key: 'driverCardExpirationDate',
        name: 'labels.expirationDate',
        required: true,
      },
    ],
  },
  {
    name: 'drivers.driverForm.identityDocuments.heading',
    fields: [
      {
        key: 'passportNumber',
        name: (translateService) =>
          `${translateService.instant('labels.passport')} (${translateService.instant('labels.number').toLowerCase()})`,
      },
      {
        key: 'passportIssuingCountry',
        name: (translateService) =>
          `${translateService.instant('labels.passport')} (${translateService.instant('labels.issuingCountry').toLowerCase()})`,
      },
      {
        key: 'passportExpirationDate',
        name: (translateService) =>
          `${translateService.instant('labels.passport')} (${translateService.instant('labels.expirationDate').toLowerCase()})`,
      },
      {
        key: 'identityDocumentNumber',
        name: (translateService) =>
          `${translateService.instant('labels.identityDocument')} (${translateService.instant('labels.number').toLowerCase()})`,
      },
      {
        key: 'identityDocumentIssuingCountry',
        name: (translateService) =>
          `${translateService.instant('labels.identityDocument')} (${translateService.instant('labels.issuingCountry').toLowerCase()})`,
      },
      {
        key: 'identityDocumentExpirationDate',
        name: (translateService) =>
          `${translateService.instant('labels.identityDocument')} (${translateService.instant('labels.expirationDate').toLowerCase()})`,
      },
      {
        key: 'residenceCardNumber',
        name: (translateService) =>
          `${translateService.instant('labels.residenceCard')} (${translateService.instant('labels.number').toLowerCase()})`,
      },
      {
        key: 'residenceCardIssuingCountry',
        name: (translateService) =>
          `${translateService.instant('labels.residenceCard')} (${translateService.instant('labels.issuingCountry').toLowerCase()})`,
      },
      {
        key: 'residenceCardExpirationDate',
        name: (translateService) =>
          `${translateService.instant('labels.residenceCard')} (${translateService.instant('labels.expirationDate').toLowerCase()})`,
      },
      {
        key: 'polishCardNumber',
        name: (translateService) =>
          `${translateService.instant('labels.polishCard')} (${translateService.instant('labels.number').toLowerCase()})`,
      },
      {
        key: 'polishCardIssuingCountry',
        name: (translateService) =>
          `${translateService.instant('labels.polishCard')} (${translateService.instant('labels.issuingCountry').toLowerCase()})`,
      },
      {
        key: 'polishCardExpirationDate',
        name: (translateService) =>
          `${translateService.instant('labels.polishCard')} (${translateService.instant('labels.expirationDate').toLowerCase()})`,
      },
    ],
    required: true,
  },
  {
    name: 'drivers.driverForm.clearCriminalRecordCertificate.heading',
    fields: [
      {
        key: 'clearCriminalRecordCertificateIssuingDate',
        name: 'labels.issueDate',
        required: true,
      },
    ],
    required: true,
  },
];
